import React from 'react';
import JwtDecode from 'jwt-decode';
import { Button, notification } from 'antd';
import moment from 'moment';
import purify from 'dompurify';
import { store } from '../molecules/Header';
import { EMPTY_OBJECT } from '../constants/Constants';
import { EMPTY_STRING } from '@tekion/tekion-base/app.constants';
import { BROWSER_TYPE } from './helper.constants';
import _escape from "lodash/escape";
import TokenManager from './TokenManager';

/**
 * @description Function to retrive roles data from cookie
 */
export const getRolesData = () => {
  const token = TokenManager.getItem('idToken');
  const decoded = token ? JwtDecode(token) : {};
  return decoded;
};

/**
 * @description Function to get check if its desktop application
 * @returns true if it is desktop Application
 */
export const isElectron = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.indexOf(' electron/') !== -1;
};

/**
 * @description Function to get email id from access token
 * @returns email id
 */
export const getEmailId = () => {
  try {
    const {
      email
    } = TokenManager.getUserDetails();
    return email;
  } catch {
    return '';
  }
};

/**
 * @description Function to get user full name
 * @returns {String} User full name
 */
export const getUserName = () => {
  try {
    const {
      header = {}
    } = store.getState();
    const {
      userDetails = {}
    } = header;
    return isEmpty(userDetails.firstName) && isEmpty(userDetails.lastName) ? '' : "".concat(userDetails === null || userDetails === void 0 ? void 0 : userDetails.firstName, " ").concat(userDetails === null || userDetails === void 0 ? void 0 : userDetails.lastName);
  } catch {
    return '';
  }
};

/**
 * @description Function to get user details
 * @returns {Object} userDetails
 */
export const getUserDetails = () => {
  try {
    const {
      header = {}
    } = store.getState();
    const {
      userDetails = {}
    } = header;
    return userDetails;
  } catch {
    return EMPTY_OBJECT;
  }
};

/**
 * @description Function to validiate user role
 * @param {Object} permissions - user role
 * @param {string} module - page
 * @param {string} action - for which action role check
 * @returns {boolean} - true / false
 */
export const validateUserPermissions = (permissions, module, action) => {
  if (permissions && Array.isArray(permissions.module)) {
    const moduleData = permissions.module.filter(permit => permit.name.includes(module))[0];
    if (moduleData && moduleData.permissions) {
      return moduleData.permissions[action];
    }
  }
  return false;
};
export const defaultOnNotificationClose = () => {
  TokenManager.clearAllCookies();
  TokenManager.clearTokenDetails();
};
export const notificationWithButton = (buttonOptions, message) => /*#__PURE__*/React.createElement(Button, {
  type: "primary",
  size: "small",
  onClick: () => {
    buttonOptions.func();
    notification.close(message);
  }
}, " ", buttonOptions.text);

/**
 * @description Function to show notification for events based on type.
 * @param {String} type - Type of notification
 * @param {String} message - title of the notification
 * @param {String} description - message to be displayed for the notification
 * @param {Number} duration - Timeout for the notification to be displayed
 * @param {Function} onNotificationClose - Method to be executed on close of the notification
 * @param {Object} buttonOptions - Button text and callBack functon for button click
 * @param {placement} placement - 'notification placement'
 */
export const notificationHelper = function (type, message, description, duration, onNotificationClose, buttonOptions) {
  let placement = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 'topLeft';
  const wrapper = notification[type];
  if (wrapper) {
    wrapper({
      message: message || 'Error',
      description: description || 'Error',
      key: message,
      duration: duration || 0,
      style: {
        width: 450,
        marginLeft: window.innerWidth / 3
      },
      placement,
      onClose: onNotificationClose || defaultOnNotificationClose,
      btn: buttonOptions ? notificationWithButton(buttonOptions, message) : null
    });
  } else {
    console.warn(__('Unsupported type passed for notification helper, type: '), type);
  }
};

/**
 * @description Email RegEx for validating proper email format.
 */
export const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * @description Function to Copy the passed content to Clipboard
 * @param {*} content - content that needs to be copied to clipboard
 * @param {*} successMsg - Message to be shown on success
 * @param {*} errorMsg - Message to be shown on error
 */
export const copyToClipBoard = content => new Promise((resolve, reject) => {
  if (!navigator.clipboard) {
    const ele = document.createElement('input');
    const parent = document.getElementById('copy__fallBack');
    ele.value = content;
    parent.appendChild(ele);
    ele.focus();
    ele.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        resolve();
      } else {
        reject();
      }
    } catch (err) {
      reject(err);
    }
    parent.removeChild(ele);
  } else {
    navigator.clipboard.writeText(content).then(() => resolve()).catch(err => reject(err));
  }
});

/**
 * @description Function to sanitize url
 * @param {String} url url that needs to be sanitized
 * @returns false/url
 */
export const sanitizeUrl = url => {
  const invalidProtocolRegex = /^([^\w]*)(javascript|data|vbscript)/im;
  const ctrlCharactersRegex = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
  const urlSchemeRegex = /^([^:]+):/gm;
  const relativeFirstCharacters = ['.', '/'];
  const isRelativeUrlWithoutProtocol = URL => relativeFirstCharacters.indexOf(URL[0]) > -1;
  if (!url) {
    return false;
  }
  const sanitizedUrl = url.replace(ctrlCharactersRegex, '').trim();
  if (isRelativeUrlWithoutProtocol(sanitizedUrl)) {
    return sanitizedUrl;
  }
  const urlSchemeParseResults = sanitizedUrl.match(urlSchemeRegex);
  if (!urlSchemeParseResults) {
    return sanitizedUrl;
  }
  const urlScheme = urlSchemeParseResults[0];
  if (invalidProtocolRegex.test(urlScheme)) {
    return false;
  }
  return validateUrl(sanitizedUrl);
};

/**
 * @description Function to validate url
 * @param {string} url - url which needs to be validated
 * @returns {boolean} - false / url
 */
export const validateUrl = url => {
  let validUrl;
  try {
    validUrl = new URL(url);
  } catch {
    return false;
  }
  if (validUrl.host === window.location.host) {
    return url;
  }
  return false;
};

/*
 * @description Function to check if given object/array/string/map value is empty or not.
 * @param {Any} value - Input value to be checked if empty / not empty.
 * @return {Boolean} - true/false
 */
export const isEmpty = value => {
  try {
    if (!value) {
      return true;
    }
    if (Array.isArray(value) && !value.length) {
      return true;
    }
    if (value.constructor.name === 'Map' && value.size === 0) {
      return true;
    }
    if (typeof value === 'object') {
      const keysArray = Object.keys(value);
      if (!keysArray.length) {
        return true;
      }
    }
    return false;
  } catch (err) {
    return false;
  }
};

/**
 * @description Function to check whether key is pressed
 * @param {object} event - event object
 * @param {number} keyCode - keycode of key to be checked
 * @return {Boolean} - true/false
 */
export const isKeyPressed = (event, keyCode) => {
  if (isEmpty(event) || isEmpty(event.keyCode) || isEmpty(keyCode)) {
    return false;
  }
  return event.keyCode === keyCode;
};

/*
 * @description Function to deep clone an object / array
 * @param {Object / Array} object
 * @returns {Object / Array} Deep cloned object / array
 */
export const deepClone = object => {
  let result = Array.isArray(object) ? [] : {};
  try {
    result = JSON.parse(JSON.stringify(object));
    return result;
  } catch (e) {
    return object;
  }
};

/*
 * @description Function to perform sanity check on browser event object of checkbox
 * @param {Object} event
 * @returns {String} target value of the event
 */
export const getSafeTargetChecked = event => event && event.target ? event.target.checked : false;

/**
 * @description function to check if provided string is html
 * @param {string} str - String to check for
 * @returns {boolean} true/false on whether it is html content
 */
export const isHTMLString = str => {
  const htmlRegex = /^</;
  return htmlRegex.test(str);
};

/**
 * @description function to default on type check
 * @param {any} item - item to check type
 * @param {any} defualtValue - defaulted value
 * @param {function} typeCheck - function to check on type fo the item
 * @returns {any} default item
 */
export const defaultTo = (item, defaultValue, typeCheck) => {
  if (typeCheck && !typeCheck(item)) {
    return defaultValue;
  }
  return item || defaultValue;
};

/**
 * @description function to detect if the userAgent is mobile or not.
 * @returns {Boolean} return true if site is opened in mobile.
 */
export const detectMobile = () => window.outerWidth < 768;

/**
 * @description Function to execute a function if condtion is true
 * @param {Boolean} condition
 * @param {Function} func
 */
export const renderIf = (condition, func) => condition ? func() : null;

/**
  * @description Function to execute a func1 if condtion is true else func2
  * @param {Boolean} condition
  * @param {Function} func1
  * @param {Function} func2
  */
export const renderIfElse = (condition, func1, func2) => condition ? func1() : func2();
export const safeCheckAndReturnListItem = (list, index, fallbackValue) => list && list.length ? list[index] : fallbackValue;

/**
 * @description Function to get formatted date using moment
 * @param {String, String} dateValue, format
 * @returns {String} formatted date
 */
export const getFormattedDate = formatDateObject => {
  const {
    dateValue = '',
    format = 'lll',
    isLocalTimezone = true,
    isRelativeTime = false
  } = formatDateObject;
  const momentDate = dateValue && moment(dateValue);
  if (isRelativeTime) {
    return momentDate ? momentDate.fromNow() : '';
  }
  if (isLocalTimezone) {
    return momentDate ? momentDate.local().format(format) : '';
  }
  return momentDate ? momentDate.utc().format(format) : '';
};

/**
 * @description Function to get Capitalized String.
 * @param {String} data
 * @returns {String} Capitalized String.
 */
export const capitalizeFirstLetter = data => {
  if (data && typeof data === 'string') {
    return data.charAt(0).toUpperCase() + data.slice(1);
  }
  return '';
};

/**
 * @description Function to convert an array of objects to an plain array by a property.
 * @param {Array} list - Array of objects
 * @param {String} key - Property of the object
 * @returns {Array} - Plain array
 */
export const constructArrayByProperty = function () {
  let list = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  if (isEmpty(list) || !Array.isArray(list)) {
    return list;
  }
  return list.map(listItem => listItem[key]);
};
/**
 * @description Function to perform sanity check on browser event object
 * @param {Object} event
 * @returns {String} target value of the event
 */
export const getSafeTargetValue = event => event && event.target ? event.target.value : '';

/**
 * @description Function to get error message for toaster
 * @param {object} err - error eobject
 * @returns {string} error message based on type of error
 */
export const getErrorToasterMessage = err => {
  if (!isEmpty(err)) {
    if (typeof err === 'string') {
      return err;
    }
    if (typeof err === 'object') {
      return err.message;
    }
    return "".concat(err);
  }
  return '';
};

/**
 * @description Function to get time value from date
 * @param {String} date
 * @returns {Number} Time value for any given date
 */
export const getTimeValueFromDate = function () {
  let date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let returnAsNumber = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  if (returnAsNumber) {
    return date && moment(date).valueOf();
  }
  return date && moment(date).valueOf().toString();
};
export const DOMPurify = purify;

/**
 * @description Function to get browser visibility and hidden prop name
 * @returns {Object} respective browser props name
 */
export const getBrowserVisibilityAndHiddenProp = () => {
  if (typeof document.hidden !== 'undefined') {
    // Opera 12.10 and Firefox 18 and later support
    return {
      visibility: 'visibilitychange',
      hidden: 'hidden'
    };
  }
  if (typeof document.msHidden !== 'undefined') {
    return {
      visibility: 'msvisibilitychange',
      hidden: 'msHidden'
    };
  }
  if (typeof document.webkitHidden !== 'undefined') {
    return {
      visibility: 'webkitvisibilitychange',
      hidden: 'webkitHidden'
    };
  }
};

/**
 * @description Function to check if document is hidden
 * @returns {Boolean} returns bool based on document visibility
 */
export const getIsDocumentHidden = () => {
  var _getBrowserVisibility;
  return !document[(_getBrowserVisibility = getBrowserVisibilityAndHiddenProp()) === null || _getBrowserVisibility === void 0 ? void 0 : _getBrowserVisibility.hidden];
};
export const getNotificationCount = count => {
  if (count > 0) {
    if (count > 99) {
      return '99+';
    }
    return count;
  }
  return EMPTY_STRING;
};

/**
 * @description Function to detect the browser type
 * @returns {String} respective browser type
 */
export const detectBrowser = () => {
  if (navigator.userAgent.indexOf(BROWSER_TYPE.EDGE) !== -1) {
    return BROWSER_TYPE.EDGE;
  } else if (navigator.userAgent.indexOf(BROWSER_TYPE.CHROME) !== -1) {
    return BROWSER_TYPE.CHROME;
  } else if (navigator.userAgent.indexOf(BROWSER_TYPE.SAFARI) !== -1) {
    return BROWSER_TYPE.SAFARI;
  } else if (navigator.userAgent.indexOf(BROWSER_TYPE.FIREFOX) !== -1) {
    return BROWSER_TYPE.FIREFOX;
  } else {
    return null;
  }
};
/*
 * @description Function to convert an object to single string
 * @param {Object} obj
 * @returns {String} obj values joined to form a string
 */
export const convertObjectToSingleString = obj => {
  const objString = Object.keys(obj).map(key => "".concat(key, ":").concat(obj[key])).join(';');
  return objString + ";";
};

/**
 * @description Function to add css style declaration
 * @param {Object, Node} obj, element
 * @returns none
 */
export const setCSSStyleDeclaration = (styleObj, dom) => {
  Object.entries(styleObj).forEach(_ref => {
    let [key, value] = _ref;
    dom.style[key] = value;
  });
};

/**
 * @description Function to inject styles in page document
 * @param {String} rel, Asset type
 * @param {String} href, Link of the asset to be injected
 * @returns none
 */
export const injectLink = function (href) {
  let rel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'stylesheet';
  const linkEl = document.createElement('link');
  linkEl.rel = rel;
  linkEl.href = href;
  document.head.appendChild(linkEl);
};

/**
 * @description Funtion to identify urls in a paragraph and replace it with anchor tag
 * @param {String} text
 * @returns String with anchor tag
 */
export const urlify = text => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, url => {
    return "<a contenteditable=\"false\" class=\"fromTodo\" href=".concat(url, " target=\"_blank\">").concat(url, "</a>");
  });
};

/**
@description Function to urlify and Domsanitize text
@param {String} text
@returns String with Sanitized version
*/
export const domSanitizeAndUrlify = html => {
  return urlify(DOMPurify.sanitize(_escape(html)));
};
/**
 * 
 * @description Function to get sorted dates
 * @param {dates} array 
 *  * @param {key} string 
 * @returns array of dates sorted by key
 */
export const sortedDates = (dates, key) => {
  const sorttedDates = dates.sort((a, b) => a[key] > b[key] ? 1 : -1);
  return sorttedDates;
};
export const getFormattedTimeIntervals = intervals => {
  const formattedIntervals = [];
  intervals.forEach(data => formattedIntervals.push({
    from: moment(data.from, 'HH:mm'),
    to: moment(data.to, 'HH:mm')
  }));
  return formattedIntervals;
};